import { ReactNode, useMemo } from 'react';

import { Box, Typography, useTheme } from '@mui/material';

import { Dimens } from '~themes';

import HTMLContainerStyle from './enums/htmlContainerStyle';

interface Props {
	content: string;
	style?: HTMLContainerStyle;
}

const HTMLContainer = ({ content, style = HTMLContainerStyle.Small }: Props) => {
	const { palette } = useTheme();

	const renderedContent = useMemo(() => {
		const renderNode = (node: ChildNode, key: string): ReactNode => {
			if (node.nodeType === Node.TEXT_NODE) {
				return node.textContent;
			}

			if (node.nodeType !== Node.ELEMENT_NODE) {
				return null;
			}

			const element = node as Element;
			const children = Array.from(element.childNodes).map((childNode, index) =>
				renderNode(childNode, `${key}-${index}`)
			);

			if (element.tagName.toLowerCase() === 'script' || element.tagName.toLowerCase() === 'style') {
				return null;
			}

			switch (style) {
				case HTMLContainerStyle.Small:
				default:
					switch (element.tagName.toLowerCase()) {
						case 'p':
							return (
								<Typography
									key={key}
									component='p'
									variant='paragraphSm'
									color={palette.textPrimary.main}
								>
									{children}
								</Typography>
							);
						case 'ul':
							return (
								<Box key={key} component='ul' sx={{ my: Dimens.spacingSm, p: 0 }}>
									{children}
								</Box>
							);
						case 'li':
							return (
								<Typography
									key={key}
									component='li'
									variant='paragraphSm'
									color={palette.textPrimary.main}
									sx={{
										display: 'flex',
										alignItems: 'flex-start',
										listStyle: 'none',
										'&::before': {
											content: '""',
											width: 4,
											height: 4,
											flexShrink: 0,
											mt: Dimens.spacingMd,
											mx: Dimens.spacingMd,
											borderRadius: Dimens.radiusFull,
											backgroundColor: 'currentColor',
										},
									}}
								>
									{children}
								</Typography>
							);
						case 'strong':
							return (
								<Box key={key} component='strong' sx={{ fontWeight: 500, display: 'inline' }}>
									{children}
								</Box>
							);
						default:
							return children;
					}
			}
		};

		if (typeof DOMParser === 'undefined') {
			return content;
		}

		const doc = new DOMParser().parseFromString(content, 'text/html');
		return Array.from(doc.body.childNodes).map((node, index) => renderNode(node, `${index}`));
	}, [content, palette.textPrimary.main, style]);

	return (
		<Box
			sx={{
				'& p': { my: 0 },
				'& p + p, & ul + p': { mt: Dimens.spacingXl },
				'& li + li': { mt: Dimens.spacingXs },
			}}
		>
			{renderedContent}
		</Box>
	);
};

export default HTMLContainer;
